import { dateTextInputSchema } from '@aspendental/shared-forms-web';
import { mountStoreDevtool } from 'simple-zustand-devtools';
import z from 'zod';
import { create } from 'zustand';

import { calculateAge, dateInvalid } from '@src/utils/date-parser';

type State = {
	firstName: string;
	lastName: string;
	dateOfBirth: Date | null;
	zipCode: string;
	geoLocation: {
		latitude: number;
		longitude: number;
	};
	patientAge: () => number | null;
	dateOfBirthString: () => string;
	email: string;
	phonenumber: string;
	hasInsurance: boolean | null;
	accountNumber: string;
	newPatient: boolean | null;
	hasScheduledAppointment: boolean;
	digiact: number;
	childPatient: boolean;
};

type Actions = {
	setFirstName: (input: string) => void;
	setLastName: (input: string) => void;
	setDateOfBirth: (input: Date) => void;
	setZipCode: (input: string) => void;
	setGeolocation: (latitude: number, longitude: number) => void;
	setEmail: (input: string) => void;
	setPhonenumber: (input: string) => void;
	setHasInsurance: (input: boolean) => void;
	setAccountNumber: (accountNumber: string) => void;
	setNewPatient: (input: boolean) => void;
	setHasScheduledAppointment: (input: boolean) => void;
	setDigiact: (input: number) => void;
	setChildPatient: (input: boolean) => void;
};
// note: GeoLocation is not a form entry so it is not in the schema below
// note: .trim() runs before the checks that follow it, so leading/trailing whitespace
// is stripped from the parsed value instead of failing validation
export const StateSchema = {
	firstName: z
		.string()
		.trim()
		.regex(/^[a-z]+$/i, 'First Name is a required property')
		.max(15, { message: 'Max of 15 characters' }),
	lastName: z
		.string()
		.trim()
		.regex(/^[a-z]+$/i, 'Last Name is a required property'),
	dateOfBirth: dateTextInputSchema.options.patientAge,
	zipCode: z.string().regex(/\d{5}/),
	zipCodeOptional: z.string().optional(),
	email: z.string().regex(/@/, 'Valid Email Address Required'), // @ symbol is only guaranteed char in an email
	phonenumber: z.string().regex(/^\d{10}$/), // API demands this for phonenumber
	hasInsurance: z.boolean(),
};

// NOTE: locale files are not accessbile from here, need to have 2 separate schemas to support error messages.
export const StateSchemaES = {
	firstName: z
		.string()
		.trim()
		.regex(/^[a-z]+$/i, 'El nombre es un campo obligatorio')
		.max(15, { message: 'Max of 15 characters' }),
	lastName: z
		.string()
		.trim()
		.regex(/^[a-z]+$/i, 'El Apellido es un campo obligatorio'),
	dateOfBirth: dateTextInputSchema.options.patientAge,
	zipCode: z.string().regex(/\d{5}/),
	zipCodeOptional: z.string().optional(),
	email: z.string().regex(/@/, 'Dirección de correo electrónico es un campo obligatorio'), // @ symbol is only guaranteed char in an email
	phonenumber: z.string().regex(/^\d{10}$/), // API demands this for phonenumber
	hasInsurance: z.boolean(),
};

const usePatientInformationFormStore = create<State & Actions>((set, get) => ({
	firstName: '',
	lastName: '',
	dateOfBirth: null,
	zipCode: '',
	geoLocation: {
		latitude: 0,
		longitude: 0,
	},
	patientAge: () => {
		if (dateInvalid(get().dateOfBirth)) {
			return null;
		} else {
			return calculateAge(get().dateOfBirth as Date);
		}
	},
	dateOfBirthString: () => {
		if (dateInvalid(get().dateOfBirth)) {
			return '';
		}

		return get().dateOfBirth?.toISOString().slice(0, 10) || '';
	},
	email: '',
	phonenumber: '',
	hasInsurance: null,
	accountNumber: '',
	newPatient: null,
	hasScheduledAppointment: false,
	digiact: 0,
	childPatient: false,
	setNewPatient: (input: boolean) => {
		set({ newPatient: input });
	},
	setHasScheduledAppointment: (input: boolean) => {
		set({ newPatient: input });
	},
	setDigiact: (input: number) => {
		set({ digiact: input });
	},
	setChildPatient: (input: boolean) => {
		set({ childPatient: input });
	},
	setFirstName: (input: string) => {
		try {
			const firstName = StateSchema.firstName.parse(input);
			set({ firstName });
		} catch (e) {
			if (e instanceof Error) {
				console.error(e.message);
			}
			throw new Error(
				'setFirstName() input did not match expected schema, make sure you are validating from StateSchema.firstName'
			);
		}
	},
	setLastName: (input: string) => {
		try {
			const { lastName } = StateSchema;
			set({ lastName: lastName.parse(input) });
		} catch (e) {
			if (e instanceof Error) {
				console.error(e.message);
			}

			throw new Error(
				'setLastName() input did not match expected schema, make sure you are validating from StateSchema.lastName'
			);
		}
	},
	setDateOfBirth: (input: Date) => {
		if (dateInvalid(input)) {
			throw new Error('setDateOfBirth() input was not a valid date object');
		}

		set({ dateOfBirth: input });
	},
	setZipCode: (input: string) => {
		try {
			const { zipCode } = StateSchema;
			zipCode.parse(input);
			set({ zipCode: input });
		} catch (e) {
			if (e instanceof Error) {
				console.error(e.message);
			}

			throw new Error(
				'setZipCode() input did not match expected schema, make sure you are validating from StateSchema.zip'
			);
		}
	},
	setGeolocation: (latitude: number, longitude: number) => {
		const geoLocation = { latitude, longitude };
		set({ geoLocation });
	},
	setEmail: (input: string) => {
		set({ email: input });
	},
	setPhonenumber: (input: string) => {
		set({ phonenumber: input });
	},
	setHasInsurance: (input: boolean) => {
		set({ hasInsurance: input });
	},
	setAccountNumber: (accountNumber: string) => set({ accountNumber }),
}));

if (process.env.NODE_ENV === 'development') {
	mountStoreDevtool('patientInformationFormStore', usePatientInformationFormStore);
}

export default usePatientInformationFormStore;
